import { useEffect, useState } from "react";
// @mui
import {
  Button,
  CircularProgress,
  Grid,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { post } from "src/utils/networkUtils";

// ----------------------------------------------------------------------

const defaultConnectionData = {
  user: "",
  password: "",
  name: "",
  server: "",
  sourceTenantId: "",
  targetTenantId: "",
  sourceSeasonId: "",
  targetSeasonId: "",
};
const DEFAULT_MATCH_BY = "externalId";
const matchByOptions = [
  { value: "externalId", label: "External ID" },
  { value: "name", label: "First + Last Name" },
];
export default function ConnectionMigrateDialog(props) {
  const {
    title,
    description,
    apiEndpoint,
    entityName,
    additionalParams = {},
    showSourceTenant = false,
    showSeason = true,
    showMatchBy = false,
    initialSourceTenantId = "",
  } = props;

  const [connectionData, setConnectionData] = useState(defaultConnectionData);
  const [matchBy, setMatchBy] = useState(DEFAULT_MATCH_BY);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  useEffect(() => {
    if (props.open && initialSourceTenantId) {
      setConnectionData((prevState) => ({
        ...prevState,
        sourceTenantId: initialSourceTenantId,
      }));
    }
  }, [props.open, initialSourceTenantId]);

  useEffect(() => {
    if (props.open) {
      setMatchBy(DEFAULT_MATCH_BY);
    }
  }, [props.open]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setConnectionData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const onSuccess = async () => {
    setLoading(true);
    try {
      const requestData = {
        ...connectionData,
        ...(showMatchBy ? { matchBy } : {}),
        ...additionalParams
      };
      const response = await post(apiEndpoint, requestData);
      setLoading(false);
      setMigrationResult(response.totalUpdated);
      setShowResult(true);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };
  const onCancel = () => {
    props.onClose();
  };

  const handleResultClose = () => {
    setShowResult(false);
    setMigrationResult(null);
    props.onClose();
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
        <DialogContentText sx={{ px: 3 }}>
          {description}
        </DialogContentText>
        <DialogContent>
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container>
              <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="user"
                  label="DB Username"
                  fullWidth
                  value={connectionData.user}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="password"
                  label="DB Password"
                  fullWidth
                  value={connectionData.password}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="name"
                  label="DB Name"
                  fullWidth
                  value={connectionData.name}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="server"
                  label="DB Server"
                  fullWidth
                  value={connectionData.server}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              {showSourceTenant && (
                <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                  <TextField
                    id="sourceTenantId"
                    label="Source Tenant"
                    fullWidth
                    value={connectionData.sourceTenantId}
                    variant="standard"
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="targetTenantId"
                  label="Target Tenant"
                  fullWidth
                  value={connectionData.targetTenantId}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              {showSeason && (
                <>
                  <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                    <TextField
                      id="sourceSeasonId"
                      label="Source SeasonId"
                      fullWidth
                      value={connectionData.sourceSeasonId}
                      variant="standard"
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                    <TextField
                      id="targetSeasonId"
                      label="Target SeasonId"
                      fullWidth
                      value={connectionData.targetSeasonId}
                      variant="standard"
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}

              {showMatchBy && (
                <Grid item xs={6} sx={{ m: 0, p: 1 }}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel id="match-by-label">Match By</InputLabel>
                    <Select
                      labelId="match-by-label"
                      id="matchBy"
                      value={matchBy}
                      label="Match By"
                      onChange={(event) => setMatchBy(event.target.value)}
                    >
                      {matchByOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCancel()}>Cancel</Button>
          <Button
            onClick={() => onSuccess()}
            autoFocus
            disabled={loading || (showSourceTenant && (!connectionData.sourceTenantId || !connectionData.targetTenantId))}
          >
            Migrate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showResult}
        onClose={handleResultClose}
        aria-labelledby="result-dialog-title"
      >
        <DialogTitle id="result-dialog-title">Migration Complete</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mt: 1 }}>
            Successfully migrated {migrationResult} {entityName}.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResultClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
